import { useEffect } from "react";

const SITE = "https://sparkbright.org";
const DEFAULT_TITLE = "Sparkwear — Where Kids Shine";
const DEFAULT_DESC =
  "Playful, pastel-perfect kidswear: clothing, essentials, school socks, bags, swimwear and baby wear.";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function useSeo({ title, description, image, jsonLd } = {}) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const fullTitle = !title || title.includes("Sparkwear") ? title || DEFAULT_TITLE : `${title} | Sparkwear`;
    const desc = description || DEFAULT_DESC;
    const path = window.location.pathname;
    const url = SITE + path;

    document.title = fullTitle;
    setMeta("name", "description", desc);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    if (image) setMeta("property", "og:image", image);
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    if (image) setMeta("name", "twitter:image", image);

    const existing = document.getElementById("page-jsonld");
    if (existing) existing.remove();
    if (jsonLdKey) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      script.textContent = jsonLdKey;
      document.head.appendChild(script);
    }
  }, [title, description, image, jsonLdKey]);
}

export function productJsonLd(products, categoryName) {
  if (!products || !products.length) return undefined;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.slice(0, 50).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: p.name,
        ...(p.image ? { image: p.image } : {}),
        description: p.tagline || `${p.name} — part of Sparkwear's pastel-perfect kidswear range, sizes 0–10Y.`,
        ...(categoryName ? { category: categoryName } : {}),
        brand: { "@type": "Brand", name: "Sparkwear" },
        offers: {
          "@type": "Offer",
          price: Number(p.price).toFixed(2),
          priceCurrency: "GBP",
          availability:
            p.stock === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
          url: `${SITE}/shop`,
        },
      },
    })),
  };
}
